// Bulle d'aide : réponses rapides et message à l'équipe Ordopli.
// Le message est enregistré dans la base de suivi d'Ordopli (table site_messages, projet Supabase de l'agent) ;
// l'agent envoie une alerte par mail à contact@ordopli.fr. La clé « anon » est publique : elle permet seulement
// d'envoyer un message, pas d'en lire.
import { useEffect, useRef, useState, type FormEvent } from "react";
import { Check, Icon, toast, useApp } from "./kit.tsx";

const SUPPORT_EMAIL = "contact@ordopli.fr";
const INBOX_URL = "https://prlqnzotpmuiwjtwlzph.supabase.co/rest/v1/site_messages";
const INBOX_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InBybHFuem90cG11aXdqdHdsenBoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3OTE3MzYwNTMsImV4cCI6MjEwNzMxMjA1M30.Onoid0R1a7aU6Zbh7tUypVbhAl_PARmugZlUjmZzNbM";

const FAQ: { q: string; a: string; href?: string; link?: string }[] = [
  { q: "Comment construire le planning de la semaine ?", a: "Créez vos jobs, puis cliquez sur « Optimiser » dans Planning : Ordopli regroupe les mêmes réglages et respecte les échéances. Vérifiez, puis « Publier » pour l'envoyer aux tablettes.", href: "#/planning", link: "Ouvrir le planning" },
  { q: "Comment installer une tablette sur un poste ?", a: "Paramètres › Tablettes des postes › « Associer une tablette ». Tapez le code à 8 chiffres sur la tablette, qui reste ensuite liée à la machine.", href: "#/parametres/tablettes", link: "Tablettes des postes" },
  { q: "Un opérateur a oublié son code ?", a: "Paramètres › Opérateurs : ouvrez sa fiche et saisissez un nouveau code à 4 chiffres. Après 5 codes erronés sur une tablette, l'opérateur y est bloqué 15 minutes.", href: "#/parametres/operateurs", link: "Opérateurs" },
  { q: "Ajouter un aléa ou un conditionnement à un poste ?", a: "Paramètres › Aléas des postes, ou Conditionnements : chaque liste se règle par type de machine, et les aléas aussi machine par machine.", href: "#/parametres/aleas", link: "Aléas des postes" },
  { q: "Donner accès à un collègue ?", a: "Paramètres › Équipe : invitez son adresse e-mail et choisissez son rôle. Les droits de chaque rôle se règlent juste en dessous.", href: "#/parametres/equipe", link: "Équipe" },
];

export function SupportBubble() {
  const { api, org } = useApp();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<"aide" | "message">("aide");
  const [msg, setMsg] = useState("");
  const [email, setEmail] = useState(api.session()?.email || "");
  const [state, setState] = useState<"idle" | "sending" | "sent">("idle");
  const panel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const f = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [open]);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!msg.trim()) return;
    setState("sending");
    const body = { kind: "support", email: email.trim(), imprimerie: org.name, page: location.hash.slice(0, 200), message: msg.trim().slice(0, 5000) };
    try {
      const r = await fetch(INBOX_URL, { method: "POST", body: JSON.stringify(body),
        headers: { "Content-Type": "application/json", apikey: INBOX_KEY, Authorization: `Bearer ${INBOX_KEY}`, Prefer: "return=minimal" } });
      if (!r.ok) throw new Error(String(r.status));
      setState("sent"); setMsg("");
    } catch {
      setState("idle");
      toast(`Envoi impossible d'ici : écrivez-nous à ${SUPPORT_EMAIL}`, "err");
    }
  };

  return (
    <div className={`support ${open ? "open" : ""}`}>
      {open && (
        <div className="support-panel" ref={panel} role="dialog" aria-label="Aide Ordopli">
          <div className="sp-head">
            <div><b>Besoin d'aide ?</b><span>L'équipe Ordopli répond en général dans la journée.</span></div>
            <button className="icon-btn light" onClick={() => setOpen(false)} aria-label="Fermer">✕</button>
          </div>
          <div className="sp-tabs">
            <button className={tab === "aide" ? "on" : ""} onClick={() => setTab("aide")}>Réponses rapides</button>
            <button className={tab === "message" ? "on" : ""} onClick={() => { setTab("message"); setState("idle"); }}>Nous écrire</button>
          </div>
          {tab === "aide" ? (
            <div className="sp-body">
              {FAQ.map((f) => (
                <details key={f.q} className="faq">
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                  {f.href && <a href={f.href} onClick={() => setOpen(false)}>{f.link}</a>}
                </details>
              ))}
              <button className="btn btn-block" onClick={() => setTab("message")}>Je ne trouve pas : écrire au support</button>
            </div>
          ) : state === "sent" ? (
            <div className="sp-body sp-sent">
              <div className="sent-check" aria-hidden="true"><Check size={28} /></div>
              <b>Message envoyé</b>
              <p className="muted">Nous vous répondons à {email || "votre adresse"}.</p>
              <button className="btn" onClick={() => setState("idle")}>Écrire un autre message</button>
            </div>
          ) : (
            <form className="sp-body" onSubmit={send}>
              <label className="field"><span className="field-label">Votre adresse</span>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></label>
              <label className="field"><span className="field-label">Votre question</span>
                <textarea rows={5} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Décrivez ce que vous cherchez à faire, ou ce qui bloque." required /></label>
              <button className="btn btn-primary btn-block" disabled={state === "sending"}>{state === "sending" ? "Envoi…" : "Envoyer"}</button>
              <p className="muted small">Ou directement : <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a></p>
            </form>
          )}
        </div>
      )}
      <button className="support-btn" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={open ? "Fermer l'aide" : "Aide et support"}>
        {open ? <Icon d="M6 6l12 12M18 6L6 18" size={22} /> : <Icon d="M12 21a9 9 0 1 0-8.2-5.3L3 21l5.3-.8A9 9 0 0 0 12 21z M9.5 9.5a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .8-1 1.5v.4 M12 16.5v.1" size={24} />}
      </button>
    </div>
  );
}
